import Phaser from 'phaser';
import { HUD_FONT } from '../fonts';
import {
  claimForgeResult,
  clearForgeIngredientSlot,
  getForgeUiState,
  runForge,
} from '../crafting/forge';
import { exitCraftingMode } from '../crafting/mode';
import { playerState } from '../player-state';
import { hudLayoutScale, hudViewport } from '../hud-viewport';
import { FORGE_UI, forgeWindowRect } from './ui-layout';
import { ensureItemTexture, fitItemIcon } from './item-textures';

const HUD_STYLE = {
  fontFamily: HUD_FONT,
  color: '#ffffff',
  stroke: '#000000',
  strokeThickness: 3,
};

const DEPTH_FORGE = 200;

export function createForgeModal(scene: Phaser.Scene, onRefresh?: () => void) {
  const { slots, forgeBtn: fb } = FORGE_UI;
  let visible = false;
  let slotSize = FORGE_UI.slotSize;
  let activeWin = forgeWindowRect();

  const root = scene.add.container(0, 0).setScrollFactor(0).setDepth(DEPTH_FORGE).setVisible(false);

  const backdrop = scene.add
    .rectangle(0, 0, 1, 1, 0x000000, 0.55)
    .setOrigin(0, 0)
    .setInteractive();

  const windowC = scene.add.container(activeWin.x, activeWin.y);
  const windowBlocker = scene.add
    .rectangle(activeWin.w / 2, activeWin.h / 2, activeWin.w, activeWin.h, 0x000000, 0.001)
    .setInteractive();

  const bg = scene.add.image(0, 0, 'ui-forge-bg').setOrigin(0, 0).setDisplaySize(activeWin.w, activeWin.h);
  const frame = scene.add.image(0, 0, 'ui-forge-frame').setOrigin(0, 0).setDisplaySize(activeWin.w, activeWin.h);

  const slotNodes: Record<
    string,
    {
      icon: Phaser.GameObjects.Image;
      cell: Phaser.GameObjects.Container;
      square: Phaser.GameObjects.Image;
      hit: Phaser.GameObjects.Rectangle;
    }
  > = {};
  const slotCells: Phaser.GameObjects.Container[] = [];

  for (const key of ['1', '2', 'result'] as const) {
    const pos = slots[key];
    const cell = scene.add.container(pos.x, pos.y);
    const square = scene.add
      .image(0, 0, 'ui-slot-square')
      .setOrigin(0, 0)
      .setDisplaySize(slotSize, slotSize);
    const icon = scene.add.image(slotSize / 2, slotSize / 2, 'ui-slot-square').setVisible(false);
    const hit = scene.add
      .rectangle(slotSize / 2, slotSize / 2, slotSize, slotSize, 0x000000, 0.001)
      .setInteractive({ useHandCursor: true });
    cell.add([square, icon, hit]);
    slotCells.push(cell);
    slotNodes[key] = { icon, cell, square, hit };

    if (key === '1') hit.on('pointerdown', () => { if (clearForgeIngredientSlot(1)) void rebuild(); });
    if (key === '2') hit.on('pointerdown', () => { if (clearForgeIngredientSlot(2)) void rebuild(); });
    if (key === 'result') {
      hit.on('pointerdown', () => {
        if (claimForgeResult()) {
          void rebuild();
          onRefresh?.();
        }
      });
    }
  }

  const forgeButton = scene.add
    .image(fb.x, fb.y, 'ui-forge-btn')
    .setOrigin(0, 0)
    .setDisplaySize(fb.w, fb.h)
    .setInteractive({ useHandCursor: true });

  const forgeLabel = scene.add
    .text(fb.x + fb.w / 2, fb.y + fb.h / 2 - 4, 'FORGE', { ...HUD_STYLE, fontSize: '28px' })
    .setOrigin(0.5);

  const dustText = scene.add.text(FORGE_UI.dustCost.x, FORGE_UI.dustCost.y, '', {
    ...HUD_STYLE,
    fontSize: '24px',
    color: '#ffe566',
    strokeThickness: 2,
  });

  windowC.add([windowBlocker, bg, frame, ...slotCells, forgeButton, forgeLabel, dustText]);
  root.add([backdrop, windowC]);

  backdrop.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    pointer.event?.stopPropagation();
    exitCraftingMode();
  });

  forgeButton.on('pointerdown', () => {
    const { canForge } = getForgeUiState();
    if (!canForge) return;
    if (runForge()) {
      void rebuild();
      onRefresh?.();
    }
  });

  function applyWindow() {
    const scale = hudLayoutScale();
    activeWin = forgeWindowRect();
    slotSize = Math.round(FORGE_UI.slotSize * scale);

    backdrop.setPosition(0, 0).setSize(hudViewport.width, hudViewport.height);
    windowC.setPosition(activeWin.x, activeWin.y);
    bg.setDisplaySize(activeWin.w, activeWin.h);
    frame.setDisplaySize(activeWin.w, activeWin.h);
    windowBlocker.setPosition(activeWin.w / 2, activeWin.h / 2).setSize(activeWin.w, activeWin.h);

    for (const key of ['1', '2', 'result'] as const) {
      const node = slotNodes[key];
      const pos = slots[key];
      node.cell.setPosition(Math.round(pos.x * scale), Math.round(pos.y * scale));
      node.square.setDisplaySize(slotSize, slotSize);
      node.icon.setPosition(slotSize / 2, slotSize / 2);
      node.hit.setPosition(slotSize / 2, slotSize / 2).setSize(slotSize, slotSize);
    }

    forgeButton
      .setPosition(Math.round(fb.x * scale), Math.round(fb.y * scale))
      .setDisplaySize(Math.round(fb.w * scale), Math.round(fb.h * scale));
    forgeLabel.setPosition(
      Math.round(fb.x * scale) + Math.round(fb.w * scale) / 2,
      Math.round(fb.y * scale) + Math.round(fb.h * scale) / 2 - 4,
    );
    forgeLabel.setFontSize(Math.round(28 * scale));
    dustText.setPosition(Math.round(FORGE_UI.dustCost.x * scale), Math.round(FORGE_UI.dustCost.y * scale));
    dustText.setFontSize(Math.round(24 * scale));
  }

  async function rebuild() {
    const ui = getForgeUiState();

    for (const key of ['1', '2'] as const) {
      const name = ui.ingredients[key];
      const node = slotNodes[key];
      if (name) {
        const tex = await ensureItemTexture(scene, name);
        if (tex) {
          node.icon.setTexture(tex).setVisible(true);
          fitItemIcon(node.icon, slotSize, 0.72);
        }
      } else {
        node.icon.setVisible(false);
      }
    }

    const resultName = ui.forgedResult || ui.resultPreview;
    const resultNode = slotNodes.result;
    if (resultName) {
      const tex = await ensureItemTexture(scene, resultName);
      if (tex) {
        resultNode.icon.setTexture(tex).setVisible(true);
        fitItemIcon(resultNode.icon, slotSize, 0.72);
        resultNode.icon.setAlpha(ui.forgedResult ? 1 : 0.55);
      }
    } else {
      resultNode.icon.setVisible(false);
    }

    if (ui.dustCost > 0 && !ui.forgedResult) {
      dustText.setText(`Dust: ${ui.dustCost}`);
      dustText.setColor(playerState.dust >= ui.dustCost ? '#ffe566' : '#ff6666');
    } else {
      dustText.setText('');
    }

    forgeButton.setAlpha(ui.canForge ? 1 : 0.45);
    forgeButton.setTint(ui.canForge ? 0xffffff : 0x888888);
  }

  return {
    root,
    open() {
      applyWindow();
      visible = true;
      root.setVisible(true);
      void rebuild();
    },
    close() {
      visible = false;
      root.setVisible(false);
    },
    isOpen: () => visible,
    refresh: rebuild,
    relayout() {
      if (!visible) return;
      applyWindow();
      void rebuild();
    },
  };
}
